import fs from "node:fs";
import path from "node:path";

import { Request } from "express";
import multer from "multer";

// File type configurations
const ALLOWED_IMAGE_TYPES = [
  // Common image formats
  "image/jpeg", // .jpg, .jpeg
  "image/jpg", // .jpg (alternative)
  "image/png", // .png
  "image/webp", // .webp
  "image/gif", // .gif
  "image/bmp", // .bmp
  "image/tiff", // .tiff, .tif
  "image/svg+xml", // .svg
  "image/x-icon", // .ico
  "image/vnd.microsoft.icon", // .ico (alternative)
];
const ALLOWED_DOCUMENT_TYPES = [
  // PDF files
  "application/pdf",
  "document/pdf", // Alternative MIME type

  // Microsoft Word documents
  "application/msword", // .doc
  "document/msword", // .doc (alternative)
  "document/doc", // .doc (alternative)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "document/docx", // .docx (alternative)

  // Microsoft Excel spreadsheets
  "application/vnd.ms-excel", // .xls
  "document/xls", // .xls (alternative)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "document/xlsx", // .xlsx (alternative)

  // Microsoft PowerPoint presentations
  "application/vnd.ms-powerpoint", // .ppt
  "document/ppt", // .ppt (alternative)
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "document/pptx", // .pptx (alternative)

  // Text files
  "text/plain", // .txt
  "document/txt", // .txt (alternative)
  "text/csv", // .csv
  "document/csv", // .csv (alternative)
  "text/rtf", // .rtf
  "application/rtf", // .rtf (alternative MIME type)
  "document/rtf", // .rtf (alternative)

  // OpenDocument formats
  "application/vnd.oasis.opendocument.text", // .odt
  "application/vnd.oasis.opendocument.spreadsheet", // .ods
  "application/vnd.oasis.opendocument.presentation", // .odp

  // Other common document formats
  "application/zip", // .zip
  "application/x-zip-compressed", // .zip (alternative)
  "application/vnd.rar", // .rar
  "application/x-rar-compressed", // .rar (alternative)
  "application/x-7z-compressed", // .7z

  // JSON and XML
  "application/json", // .json
  "application/xml", // .xml
  "text/xml", // .xml (alternative)
];
// Note: Profile documents (for school, teacher, student profiles) accept both document and image types

// File size limits (in bytes)
const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB (increased from 5MB)
const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024; // 50MB (increased from 10MB)

// Ensure upload directories exist
const ensureUploadDirs = () => {
  const dirs = [
    path.join(process.cwd(), "uploads"),
    path.join(process.cwd(), "uploads", "profiles"),
    path.join(process.cwd(), "uploads", "documents"),
    path.join(process.cwd(), "uploads", "logos"),
    path.join(process.cwd(), "uploads", "materials"),
    path.join(process.cwd(), "uploads", "syllabus"),
    path.join(process.cwd(), "uploads", "chat"),
  ];

  for (const dir of dirs) {
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }
  }
};

// Initialize upload directories
ensureUploadDirs();

// Storage configuration
const storage = multer.diskStorage({
  destination: (
    req: Request,
    file: Express.Multer.File,
    cb: (error: Error | null, destination: string) => void,
  ) => {
    let uploadPath = "uploads/";

    // Determine upload path based on field name
    switch (file.fieldname) {
      case "profileImage": {
        uploadPath += "profiles/";
        break;
      }
      case "logo": {
        uploadPath += "logos/";
        break;
      }
      case "documents": {
        uploadPath += "documents/";
        break;
      }
      case "materials": {
        uploadPath += "materials/";
        break;
      }
      case "attachments": {
        // Backward compatibility: treat attachments as documents
        uploadPath += "documents/";
        break;
      }
      case "syllabusFile": {
        uploadPath += "syllabus/";
        break;
      }
      case "chatFiles":
      case "chatAttachments": {
        uploadPath += "chat/";
        break;
      }
      case "files":
      case "materialFile":
      case "quizMaterial":
      case "submittedFile": {
        uploadPath += "documents/";
        break;
      }
      default: {
        uploadPath += "misc/";
      }
    }

    // Ensure the specific directory exists
    const fullPath = path.join(process.cwd(), uploadPath);
    if (!fs.existsSync(fullPath)) {
      fs.mkdirSync(fullPath, { recursive: true });
    }

    cb(null, uploadPath);
  },
  filename: (
    req: Request,
    file: Express.Multer.File,
    cb: (error: Error | null, filename: string) => void,
  ) => {
    // Generate unique filename with timestamp and random string
    const uniqueSuffix = Date.now() + "-" + Math.round(Math.random() * 1e9);
    const ext = path.extname(file.originalname);
    const name = file.fieldname + "-" + uniqueSuffix + ext;
    cb(null, name);
  },
});

// File filter function
const fileFilter = (
  req: Request,
  file: Express.Multer.File,
  cb: multer.FileFilterCallback,
) => {
  let isAllowed = false;
  let errorMessage = "Invalid file type";

  // Check file type based on field name and mime type
  switch (file.fieldname) {
    case "profileImage":
    case "logo": {
      isAllowed = ALLOWED_IMAGE_TYPES.includes(file.mimetype);
      errorMessage = `Only ${ALLOWED_IMAGE_TYPES.join(", ")} files are allowed for images`;
      break;
    }
    case "documents": {
      // Allow both documents and images for profile documents
      const allowedProfileDocTypes = [
        ...ALLOWED_DOCUMENT_TYPES,
        ...ALLOWED_IMAGE_TYPES,
      ];
      isAllowed = allowedProfileDocTypes.includes(file.mimetype);
      errorMessage = `Only ${allowedProfileDocTypes.join(", ")} files are allowed for documents`;
      break;
    }
    case "attachments": {
      // Class withdrawal, messages, etc.: same as documents (PDF/Office + images)
      const allowedAttachmentTypes = [
        ...ALLOWED_DOCUMENT_TYPES,
        ...ALLOWED_IMAGE_TYPES,
      ];
      isAllowed = allowedAttachmentTypes.includes(file.mimetype);
      errorMessage =
        "Only documents and images are allowed for attachments (e.g. PDF, Word, images).";
      break;
    }
    case "syllabusFile": {
      isAllowed = ALLOWED_DOCUMENT_TYPES.includes(file.mimetype);
      errorMessage = `Only document files (PDF, Word, etc.) are allowed for syllabus`;
      break;
    }
    case "materials": {
      // Course materials: allow documents and images (same as profile documents)
      const allowedMaterialTypes = [
        ...ALLOWED_DOCUMENT_TYPES,
        ...ALLOWED_IMAGE_TYPES,
      ];
      isAllowed = allowedMaterialTypes.includes(file.mimetype);
      errorMessage = `Only documents and images are allowed for course materials`;
      break;
    }
    case "chatFiles":
    case "chatAttachments": {
      // Allow images, documents, audio, and video for chat
      const ALLOWED_AUDIO_TYPES = [
        "audio/mpeg",
        "audio/mp3",
        "audio/wav",
        "audio/ogg",
        "audio/webm",
        "audio/aac",
        "audio/m4a",
        "audio/mp4",
        "audio/x-m4a",
        "audio/x-caf",
      ];
      const ALLOWED_VIDEO_TYPES = [
        "video/mp4",
        "video/webm",
        "video/ogg",
        "video/quicktime",
        "video/x-msvideo",
        "video/x-matroska",
      ];
      const allowedChatTypes = [
        ...ALLOWED_DOCUMENT_TYPES,
        ...ALLOWED_IMAGE_TYPES,
        ...ALLOWED_AUDIO_TYPES,
        ...ALLOWED_VIDEO_TYPES,
      ];
      isAllowed = allowedChatTypes.includes(file.mimetype);
      errorMessage = `Only images, documents, audio, and video files are allowed for chat attachments`;
      break;
    }
    case "files":
    case "materialFile":
    case "quizMaterial":
    case "submittedFile": {
      const allowedAssignmentTypes = [
        "application/pdf",
        "document/pdf",
        "application/msword",
        "document/msword",
        "document/doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "document/docx",
        ...ALLOWED_IMAGE_TYPES,
      ];
      isAllowed = allowedAssignmentTypes.includes(file.mimetype);
      errorMessage =
        "Only PDF, DOC, DOCX, and image files are allowed for assignments/quizzes";
      break;
    }
    default: {
      isAllowed = false;
      errorMessage = "Unknown file field";
    }
  }

  if (isAllowed) {
    cb(null, true);
  } else {
    cb(new Error(errorMessage));
  }
};

// Create multer instances for different upload types
const createUploader = (
  fields: { name: string; maxCount: number }[],
  maxFileSize: number,
) => {
  return multer({
    storage,
    fileFilter,
    limits: {
      fileSize: maxFileSize,
      files: fields.reduce((sum, field) => sum + field.maxCount, 0),
    },
  }).fields(fields);
};

// Profile image uploader (for all profiles)
export const uploadProfileImage = multer({
  storage,
  fileFilter,
  limits: {
    fileSize: MAX_IMAGE_SIZE,
    files: 1,
  },
}).single("profileImage");

// School profile uploader (logo + documents)
export const uploadSchoolFiles = createUploader(
  [
    { name: "logo", maxCount: 1 },
    { name: "documents", maxCount: 10 },
  ],
  Math.max(MAX_IMAGE_SIZE, MAX_DOCUMENT_SIZE),
);

// Teacher profile uploader (profile image + documents)
export const uploadTeacherFiles = createUploader(
  [
    { name: "profileImage", maxCount: 1 },
    { name: "documents", maxCount: 10 },
  ],
  Math.max(MAX_IMAGE_SIZE, MAX_DOCUMENT_SIZE),
);

// Student profile uploader (profile image + documents)
export const uploadStudentFiles = createUploader(
  [
    { name: "profileImage", maxCount: 1 },
    { name: "documents", maxCount: 10 },
  ],
  Math.max(MAX_IMAGE_SIZE, MAX_DOCUMENT_SIZE),
);

// Personal profile uploader (profile image only)
export const uploadPersonalFiles = uploadProfileImage;

// General uploader for class materials and other files
export const uploadMiddleware = multer({
  storage,
  fileFilter,
  limits: {
    fileSize: Math.max(MAX_IMAGE_SIZE, MAX_DOCUMENT_SIZE),
    files: 10,
  },
});

// Assignment material uploader (for teacher creating/updating assignments)
export const uploadAssignmentMaterial = createUploader(
  [{ name: "materialFile", maxCount: 5 }],
  MAX_DOCUMENT_SIZE,
);

// Quiz material uploader (for teacher creating/updating quizzes)
export const uploadQuizMaterial = createUploader(
  [{ name: "quizMaterial", maxCount: 5 }],
  MAX_DOCUMENT_SIZE,
);

// Student assignment submission uploader
export const uploadSubmittedFile = createUploader(
  [{ name: "submittedFile", maxCount: 5 }],
  MAX_DOCUMENT_SIZE,
);

// Chat file uploader (supports images, documents, audio, video)
export const uploadChatFiles = createUploader(
  [{ name: "chatFiles", maxCount: 10 }],
  Math.max(MAX_IMAGE_SIZE, MAX_DOCUMENT_SIZE),
);

// Course syllabus uploader (single optional document)
export const uploadCourseSyllabus = createUploader(
  [{ name: "syllabusFile", maxCount: 1 }],
  MAX_DOCUMENT_SIZE,
);

// Helper function to process uploaded files and create file info objects
export const processUploadedFiles = (files: any): any => {
  if (!files) return {};

  const result: any = {};

  // Process single files
  if (files.profileImage?.[0]) {
    result.profilePicture = createFileInfo(files.profileImage[0]);
  }

  if (files.logo?.[0]) {
    result.logo = createFileInfo(files.logo[0]);
  }

  // Process multiple files (documents)
  if (files.documents && Array.isArray(files.documents)) {
    result.documents = files.documents.map((file: Express.Multer.File) =>
      createFileInfo(file),
    );
  }

  // Process chat files
  if (files.chatFiles && Array.isArray(files.chatFiles)) {
    result.chatFiles = files.chatFiles.map((file: Express.Multer.File) =>
      createFileInfo(file),
    );
  }

  if (files.chatAttachments && Array.isArray(files.chatAttachments)) {
    result.chatAttachments = files.chatAttachments.map(
      (file: Express.Multer.File) => createFileInfo(file),
    );
  }

  // Process syllabus file (single file)
  if (files.syllabusFile?.[0]) {
    result.syllabusFile = createFileInfo(files.syllabusFile[0]);
  }

  return result;
};

// Helper function to create file info object
const createFileInfo = (file: Express.Multer.File) => {
  // Extract the subdirectory from destination (e.g., "uploads/profiles/" -> "profiles/")
  let subDir = file.destination || "";

  // Remove "uploads/" or "uploads\" prefix
  subDir = subDir.replace(/^uploads[\/\\]/, "");

  // Ensure subDir ends with / if it's not empty
  if (subDir && !subDir.endsWith("/") && !subDir.endsWith("\\")) {
    subDir += "/";
  }

  // Normalize path separators to forward slash
  subDir = subDir.replaceAll("\\", "/");

  // Get base URL from environment (should include protocol, domain, port, and /uploads)
  // Example: https://api.covalent.prodservers.com:3091/uploads
  const baseUrl = process.env.IMAGE_URL || "http://localhost:3080/uploads";

  // Construct full URL: baseUrl + subdirectory + filename
  const fileUrl = `${baseUrl}/${subDir}${file.filename}`;

  console.log("📁 File Upload:", {
    fieldname: file.fieldname,
    filename: file.filename,
    subDir: subDir,
    fileUrl: fileUrl,
  });

  return {
    fileName: file.filename,
    originalName: file.originalname,
    fileSize: file.size,
    mimeType: file.mimetype,
    fileUrl,
    uploadedAt: new Date(),
  };
};

// Helper function to handle upload errors
export const handleUploadError = (error: any) => {
  if (error instanceof multer.MulterError) {
    switch (error.code) {
      case "LIMIT_FILE_SIZE": {
        return {
          status: 413,
          message: `File size too large. Maximum allowed size is ${Math.floor(MAX_IMAGE_SIZE / (1024 * 1024))}MB for images and ${Math.floor(MAX_DOCUMENT_SIZE / (1024 * 1024))}MB for documents.`,
        };
      }
      case "LIMIT_FILE_COUNT": {
        return {
          status: 400,
          message:
            "Too many files uploaded. Please reduce the number of files.",
        };
      }
      case "LIMIT_UNEXPECTED_FILE": {
        return {
          status: 400,
          message: "Unexpected file field. Please check your form data.",
        };
      }
      default: {
        return {
          status: 400,
          message: "File upload error: " + error.message,
        };
      }
    }
  } else if (error.message.includes("Unknown file field")) {
    return {
      status: 400,
      message: error.message,
    };
  } else if (error.message.includes("file type")) {
    return {
      status: 400,
      message: error.message,
    };
  } else {
    return {
      status: 500,
      message: "Internal server error during file upload",
    };
  }
};
